import { useEffect, useState, useMemo } from "react";
import {
  LdsButton,
  LdsCard,
  useLdsModal,
  useToastContext,
  LdsToast,
  LdsIcon,
} from "@elilillyco/ux-lds-react";
import Select from "react-select";
import { useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";

import {
  CreateListModal,
  CreateNewListModal,
  OverviewTile,
} from "../components/overview";

import {
  FETCH_OVERVIEW,
  SET_BRAND,
  FETCH_EXPORT_SPECIALTY_LIST,
  SET_LOADING,
} from "../store/overview";

import { FETCH_THPC_LIST, FETCH_USER_LIST } from "../store/admin";

import "./../assets/scss/overview/overview.scss";
import CLoader from "../components/shared/CLoader";
import infoToastConfig from "../components/shared/Toast";

const Overview = () => {
  const [userBrands, setUserBrands] = useState([]);
  const [isExistingCreateList, setIsExistingCreateList] = useState("");
  const [, setDropdownError] = useState(false);
  const [selectedUserForFilter, setSelectedUserForFilter] = useState("");

  /**
   * Checks if current logged in user is admin or super admin
   *
   * @returns true if user is admin or super admin else false
   */
  const isAdminRole = () =>
    sessionStorage.getItem("role") === "spclty_hub_admin" ||
    sessionStorage.getItem("role") === "spclty_hub_super_admin";

  // Conditionally set filters based on role - determined at component initialization
  const filtersArray = useMemo(() => {
    return isAdminRole()
      ? ["SALESFORCE", "MLR", "APPROVED", "DRAFT", "REQUEST FOR REVIEW", "REQUEST FOR APPROVAL"]
      : ["SALESFORCE", "MLR", "APPROVED", "DRAFT", "REQUEST FOR REVIEW", "REQUEST FOR APPROVAL", "My Lists"];
  }, []);

  const [filters, setFilters] = useState(() => {
    const initialFilters = {};
    filtersArray.forEach(filter => {
      initialFilters[filter] = false;
    });
    return initialFilters;
  });

  const dispatch = useDispatch();

  const { brand, allBrands } = useSelector(({ user }) => user.user || {});
  const { thpc, thpcLoading, Brands, userTableList } = useSelector(({ admin }) => admin);
  const { current, historical, loading, selectedBrand } = useSelector(
    ({ overview }) => overview
  );

  const [selectedthpc, setSelectedthpc] = useState("");

  const [searchParams] = useSearchParams();

  const { addToast } = useToastContext();

  const {
    isModalOpen: isCreateNewListModalOpen,
    setIsModalOpen: setIsCreateNewListModalOpen,
  } = useLdsModal();

  const {
    isModalOpen: isCreateListModalOpen,
    setIsModalOpen: setIsCreateListModalOpen,
  } = useLdsModal();

  const {
    isModalOpen: isDeleteModalOpen,
    setIsModalOpen: setIsDeleteModalOpen,
  } = useLdsModal();

  /**
   * Checks if user can export all specialty lists
   * Roles: Admin, Super Admin, BIA Team
   *
   * @returns true if user can export all lists
   */
  const canExportAllLists = () => {
    const role = sessionStorage.getItem("role");
    return role === "spclty_hub_admin" ||
      role === "spclty_hub_super_admin" ||
      role === "spclty_hub_bia_team";
  };

  /**
   * Checks if current user role can create lists
   * Roles that CANNOT create lists: viewer, mlr_approver, mlr_reviewer, bia_team, incentivized_approver
   *
   * @returns true if user can create lists else false
   */
  const canCreateList = () => {
    const role = sessionStorage.getItem("role");
    const restrictedRoles = [
      "spclty_hub_viewer",
      "spclty_hub_mlr_approver",
      "spclty_hub_mlr_reviewer",
      "spclty_hub_bia_team",
      "spclty_hub_incentivized_approver"
    ];
    return !restrictedRoles.includes(role);
  };

  const openCreateNewListModal = () => {
    setIsCreateNewListModalOpen(true);
  };

  const openCreateListModal = (modalType) => {
    if (modalType && modalType === "existing") {
      setIsExistingCreateList(true);
    } else {
      setIsExistingCreateList(false);
    }
    setIsCreateNewListModalOpen(false);
    setIsCreateListModalOpen(true);
  };

  const showSuccessToast = () => {
    addToast(
      infoToastConfig("Your changes have been saved successfully.", "success")
    );
  };

  const navigateToCreateListModal = () => {
    setIsCreateListModalOpen(false);
    setIsCreateNewListModalOpen(true);
  };

  const filterLabels = {
    SALESFORCE: "Salesforce",
    MLR: "MLR",
    APPROVED: "Approved",
    DRAFT: "Draft",
    "REQUEST FOR REVIEW": "Request for Review",
    "REQUEST FOR APPROVAL": "Request for Approval",
    "My Lists": "My Lists",
  };

  const filterRecords = (filter) => {
    const filtersCopy = { ...filters };
    filtersCopy[filter] = !filtersCopy[filter];
    setFilters(filtersCopy);
  };

  const resetFilters = () => {
    const filtersCopy = { ...filters };
    Object.keys(filtersCopy).forEach((filter) => (filtersCopy[filter] = false));
    setFilters(filtersCopy);
  };

  const clearAllFilters = () => {
    // Clear status chips
    resetFilters();
    // Clear THPC dropdown
    setSelectedthpc('');
    // Clear user filter dropdown (admin only)
    setSelectedUserForFilter('');
    // Clear brand selection and refetch
    dispatch(SET_BRAND(''));
    setDropdownError(false);
    if (isAdminRole()) {
      dispatch(
        FETCH_OVERVIEW({
          thpc: '',
          brand: '',
          userBrands: [],
        })
      );
    } else {
      dispatch(
        FETCH_OVERVIEW({
          thpc: '',
          brand: '',
          userBrands: userBrands,
        })
      );
    }
  };

  const hasAnyFilterApplied = () => {
    return (
      Object.values(filters).some((v) => v) ||
      !!selectedthpc ||
      !!selectedBrand ||
      !!selectedUserForFilter
    );
  };

  useEffect(() => {
    const role = sessionStorage.getItem("role");

    // If brand data hasn't loaded yet, wait
    if (!brand || !Object.keys(brand).length) {
      return;
    }

    // Check if user has brands for their role
    if (brand[role]) {
      const brandValue = brand[role].trim();

      // Check if user has ALL brands (backend sends "ALL" as value)
      const hasAllBrands = brandValue === "ALL";

      // Get the actual brand list
      let currentBrands;
      if (hasAllBrands && allBrands) {
        // User has ALL brands - use allBrands list from backend
        currentBrands = allBrands.split(",").filter((b) => b.trim());
      } else if (brandValue) {
        // User has specific brands
        currentBrands = brandValue.split(",").filter((b) => b.trim());
      } else {
        // User has role but no brands assigned
        currentBrands = [];
      }

      if (currentBrands.length) {
        const urlBrand = searchParams.get("brand")?.toUpperCase();

        if (urlBrand && urlBrand.trim() && (currentBrands.includes(urlBrand) || hasAllBrands)) {
          // Only set specific brand if it's in URL and valid
          dispatch(SET_BRAND(urlBrand));
          dispatch(
            FETCH_OVERVIEW({
              brand: urlBrand,
              userBrands: currentBrands,
            })
          );
        } else if (urlBrand && !currentBrands.includes(urlBrand) && !hasAllBrands) {
          // URL brand is not valid for this user
          setDropdownError(true);
          dispatch(SET_BRAND(''));  // Set to empty for "Select Brand/Molecule"
          dispatch(SET_LOADING(false));
          addToast(infoToastConfig("Brand not tagged.", "error"));
        } else {
          // No URL brand parameter
          if (isAdminRole()) {
            // Admin/Super Admin - default to empty to show all lists
            dispatch(SET_BRAND(''));
            dispatch(
              FETCH_OVERVIEW({
                brand: '',  // Fetch all brands for admins
                userBrands: [],
              })
            );
          } else {
            // Non-admin users - default to empty to show all their assigned brands + THPC-only lists
            dispatch(SET_BRAND(''));
            dispatch(
              FETCH_OVERVIEW({
                brand: '',  // Empty = show all user's brands
                userBrands: currentBrands,
              })
            );
          }
        }
        setUserBrands(currentBrands);
      } else {
        // User has role in brand object but no brands assigned (empty string)
        setUserBrands([]);
        dispatch(SET_LOADING(false));
        addToast(infoToastConfig("No brand/molecule are assigned to your role.", "warning"));
      }
    } else {
      // User's role is not in the brand object at all - no brands assigned
      setUserBrands([]);
      dispatch(SET_LOADING(false));
      addToast(infoToastConfig("No brand/molecule are assigned to your role.", "warning"));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [brand, allBrands, Brands]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    dispatch(FETCH_THPC_LIST()).then(() => {
      setSelectedthpc('');  // Default to empty string for "Select Therapeutic Area"
    });
    // Fetch user list for admin role
    if (isAdminRole()) {
      dispatch(FETCH_USER_LIST());
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div id="main">
      <section className="section">
        <div className="wrapper overview-page">
          <div>
            <div className="row">
              <div className="col col-12 col-md">
                <div>
                  <div>
                    <div className="row">
                      <div className="col-6">
                        <h2>Overview</h2>
                        {/* Card content  */}
                      </div>
                      {userBrands?.length > 0 && (
                        <>
                          <div className="col-12">
                          <div className="parent-filters-container">
                            <div className="combined-filters-label">
                              <LdsIcon name="FunnelSimple" size={14} />
                              <span>Filters</span>
                            </div>
                            <div className="combined-filters-pipe-tall" aria-hidden="true" />
                            <div className="combined-filters-content">
                              <div className="combined-row combined-row--top">
                                <div className="combined-dropdowns">
                          <div className="parent-filters-field create-list-fields">
                            <label htmlFor="select-thpc" className="form-label">Select Therapeutic Area</label>
                            <Select
                              id="select-thpc"
                              name="select-thpc"
                              value={thpc ? thpc.find(option => option.value === selectedthpc) || { value: '', label: 'Select Therapeutic Area' } : null}
                              isDisabled={thpcLoading || loading || !thpc || thpc.length === 0}
                              options={thpc ? [
                                { value: '', label: 'Select Therapeutic Area' },
                                ...thpc.filter(option =>
                                  !(option.label && option.label.toLowerCase().includes('select'))
                                )
                              ] : []}
                              onChange={(selectedOption) => {
                                const value = selectedOption ? selectedOption.value : '';
                                setSelectedthpc(value);

                                // Fetch based on user role and brand selection
                                if (selectedBrand) {
                                  // Brand is selected - fetch with brand filter
                                  dispatch(
                                    FETCH_OVERVIEW({
                                      thpc: value || '',
                                      brand: selectedBrand,
                                      userBrands: userBrands,
                                    })
                                  );
                                } else if (isAdminRole()) {
                                  // Admin with no brand selected - can fetch all
                                  dispatch(
                                    FETCH_OVERVIEW({
                                      thpc: value || '',
                                      brand: '',
                                      userBrands: [],
                                    })
                                  );
                                } else {
                                  // Non-admin without brand selection - fetch all their brands + THPC-only lists
                                  dispatch(
                                    FETCH_OVERVIEW({
                                      thpc: value || '',
                                      brand: '',
                                      userBrands: userBrands,
                                    })
                                  );
                                }
                                resetFilters();
                              }}
                              placeholder="Select Therapeutic Area"
                              isClearable
                            />
                          </div>
                          <div className="parent-filters-field create-list-fields">
                            <label htmlFor="select-brand" className="form-label">Select Brand/Molecule</label>
                            <Select
                              id="select-brand"
                              name="select-brand"
                              value={
                                userBrands.length > 0
                                  ? userBrands.map(brand => ({ value: brand, label: brand })).find(option => option.value === selectedBrand) || null
                                  : null
                              }
                              isDisabled={loading || userBrands.length === 0}
                              options={userBrands.map((option) => ({
                                value: option,
                                label: option,
                              }))}
                              onChange={(selectedOption) => {
                                const value = selectedOption ? selectedOption.value : '';
                                dispatch(SET_BRAND(value));
                                setDropdownError(false);

                                // Fetch data based on user role and selection
                                if (value) {
                                  // Specific brand selected - fetch lists for that brand
                                  dispatch(
                                    FETCH_OVERVIEW({
                                      thpc: selectedthpc,
                                      brand: value,
                                      userBrands: userBrands, // Pass user brands for context
                                    })
                                  );
                                } else if (isAdminRole()) {
                                  // Admin/Super Admin with empty selection - fetch ALL lists
                                  dispatch(
                                    FETCH_OVERVIEW({
                                      thpc: selectedthpc,
                                      brand: '',  // Empty brand = all brands for admins
                                      userBrands: [], // Admins don't need brand filtering
                                    })
                                  );
                                } else {
                                  // Non-admin users with empty selection - fetch lists for ALL their assigned brands
                                  // PLUS lists with only THPC/DSST (no brand/molecule)
                                  dispatch(
                                    FETCH_OVERVIEW({
                                      thpc: selectedthpc,
                                      brand: '',  // Empty brand triggers multi-brand logic
                                      userBrands: userBrands, // Send user's assigned brands for backend filtering
                                    })
                                  );
                                }
                                resetFilters();
                              }}
                              placeholder="Select Brand/Molecule"
                              isClearable
                            />
                          </div>
                          {isAdminRole() && userTableList && userTableList.length > 0 && (
                            <div className="parent-filters-field create-list-fields">
                              <label htmlFor="select-user-filter" className="form-label">Filter by User</label>
                              <Select
                                id="select-user-filter"
                                name="select-user-filter"
                                value={
                                  selectedUserForFilter
                                    ? (() => {
                                      const uniqueUsers = [];
                                      const seenEmails = new Set();
                                      userTableList.forEach(user => {
                                        if (user.email_id && !seenEmails.has(user.email_id.toLowerCase())) {
                                          seenEmails.add(user.email_id.toLowerCase());
                                          uniqueUsers.push(user);
                                        }
                                      });
                                      return uniqueUsers
                                        .map(user => ({
                                          value: user.email_id,
                                          label: `${user.first_name} ${user.last_name}`.trim()
                                        }))
                                        .find(option => option.value === selectedUserForFilter) || null;
                                    })()
                                    : null
                                }
                                options={(() => {
                                  const uniqueUsers = [];
                                  const seenEmails = new Set();
                                  userTableList.forEach(user => {
                                    if (user.email_id && !seenEmails.has(user.email_id.toLowerCase())) {
                                      seenEmails.add(user.email_id.toLowerCase());
                                      uniqueUsers.push(user);
                                    }
                                  });
                                  return uniqueUsers
                                    .map(user => ({
                                      value: user.email_id,
                                      label: `${user.first_name} ${user.last_name}`.trim()
                                    }))
                                    .sort((a, b) => a.label.localeCompare(b.label));
                                })()}
                                onChange={(selectedOption) => {
                                  const value = selectedOption ? selectedOption.value : '';
                                  setSelectedUserForFilter(value);
                                }}
                                placeholder="Filter by User"
                                isClearable
                              />
                            </div>
                          )}
                            </div>
                          </div>
                              <div className="combined-row combined-row--bottom">
                                <div className="combined-status-filters">
                                  <div className="filter-chips">
                                    {filtersArray.map((filter) => (
                                      <button
                                        key={filter}
                                        type="button"
                                        className={`filter-chip${filters[filter] ? " filter-chip--active" : ""}`}
                                        onClick={() => filterRecords(filter)}
                                      >
                                        {filterLabels[filter] || filter}
                                      </button>
                                    ))}
                                  </div>
                                </div>
                              </div>
                            </div>
                            <div className="combined-filters-pipe-tall" aria-hidden="true" />
                            <div className="combined-clear-all">
                              <button
                                type="button"
                                className="filter-reset-btn"
                                onClick={clearAllFilters}
                                disabled={!hasAnyFilterApplied()}
                              >
                                Clear all
                              </button>
                            </div>
                          </div>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="mt-4">
            {loading ? (
              <CLoader size={90} />
            ) : userBrands.length === 0 ? (
              <div className="content-block">
                <LdsCard>
                  <LdsCard.Body>
                    <div className="row">
                      <div className="col-12 text-center" style={{ padding: '2rem' }}>
                        <h3>No Data Available</h3>
                        <p style={{ marginTop: '1rem', color: '#666' }}>
                          No brand/molecule are assigned to your role. Please assign brand/molecule to yourself.
                        </p>
                      </div>
                    </div>
                  </LdsCard.Body>
                </LdsCard>
              </div>
            ) : (
              <div className="content-block">
                <div className="row">
                  <div className="col col-12 col-md">
                    <LdsCard>
                      <LdsCard.Body>
                        <div className="row">
                          <div className="col-6">
                            <h2>Current</h2>
                          </div>
                          <div className="col-6 create-list create-list-actions">
                            {canExportAllLists() && (
                              <LdsButton
                                classes="button-icon-left blue-btn"
                                icon="DownloadSimple"
                                iconPosition="before"
                                onClick={() =>
                                  dispatch(FETCH_EXPORT_SPECIALTY_LIST())
                                }
                              >
                                Export
                              </LdsButton>
                            )}
                            {canCreateList() && (
                              <LdsButton
                                icon="Plus"
                                iconPosition="before"
                                classes="button-icon-left blue-btn"
                                onClick={openCreateNewListModal}
                              >
                                Create new list
                              </LdsButton>
                            )}
                          </div>
                        </div>
                        <OverviewTile
                          listRecords={current}
                          isModalOpen={isDeleteModalOpen}
                          setIsModalOpen={setIsDeleteModalOpen}
                          showSuccessToast={showSuccessToast}
                          filters={filters}
                          isCurrent={true}
                          selectedUserForFilter={selectedUserForFilter}
                          resetFilters={resetFilters}
                        />
                      </LdsCard.Body>
                    </LdsCard>
                  </div>
                </div>
                <div className="row mtop-3">
                  <div className="col col-12 col-md">
                    <LdsCard>
                      <LdsCard.Body>
                        <div className="row">
                          <div className="col-12">
                            <h2>Historical</h2>
                          </div>
                        </div>
                        <OverviewTile
                          listRecords={historical}
                          filters={filters}
                          isCurrent={false}
                          selectedUserForFilter={selectedUserForFilter}
                        />
                      </LdsCard.Body>
                    </LdsCard>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
        <CreateNewListModal
          isModalOpen={isCreateNewListModalOpen}
          setIsModalOpen={setIsCreateNewListModalOpen}
          openCreateListModal={openCreateListModal}
        />
        <CreateListModal
          isModalOpen={isCreateListModalOpen}
          setIsModalOpen={setIsCreateListModalOpen}
          modalType={isExistingCreateList}
          navigateBack={navigateToCreateListModal}
        />
      </section>
      <LdsToast />
    </div>
  );
};

export default Overview;